import { useMemo, useState } from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import { useGetOrganList } from "../../../../../services/api-tansquery/get-list-organs";
import { useDebouncedValue } from "../../../../../hooks/useDebounce";
import { AttendanceLocal } from "../../../../../@types/attendance-local";

type OrganFieldProps = {
    field: ControllerRenderProps<{
        slug_unidade?: string;
        ativo?: boolean;
        source?: string;
        nome?: string;
        endereco?: string;
        bairro?: string;
        cep?: string;
        orgao_id?: number;
        cidade?: {
            slug?: string;
            cidade?: string;
            estado?: string;
        };
        complemento?: string;
    }, "orgao_id">;
    fieldState: ControllerFieldState;
    organ?: AttendanceLocal["orgao"];
    style?: React.CSSProperties;
};

export default function OrganField({
    field,
    fieldState,
    style,
    organ
}: OrganFieldProps) {
    const [search, setSearch] = useState<string>();
    const debouncedSearch = useDebouncedValue(search, 500);
    const { organList, isFetching: isLoading } = useGetOrganList({
        search: debouncedSearch, page: 1,
    });

    const options = useMemo(() => {
        const merged = [...organList, ...(organ ? [organ] : [])];
        return merged.filter(
            (item, index, self) => self.findIndex(i => i.id === item.id) === index
        );
    }, [organList, organ]);

    const selected = useMemo(() => {
        return options.find((item) => item.id === field.value) || null;
    }, [options, field.value]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value.trim() === "" ? undefined : value);
        }
    }

    return (
        <Autocomplete
            style={style}
            freeSolo={false}
            options={options}
            value={selected}
            getOptionLabel={(option) => option.nome || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={(_, newValue) => {
                field.onChange(newValue ? newValue.id : null);
            }}
            onInputChange={handleInputChange}
            loading={isLoading}
            loadingText="Carregando órgãos..."
            noOptionsText="Nenhum órgão encontrado"
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Órgão"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={
                        fieldState.error?.message ||
                        "Informe o órgão responsável por este local de atendimento"
                    }
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        endAdornment: (
                            <>
                                {isLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
